import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HotjarSettings } from '../model/hotjar-settings';

/**
 * Provides a token to override default settings. You can use this token to enhance our library
 * and configure multiple sites/app on the same environment.
 */
export const HOTJAR_SETTINGS = new InjectionToken<HotjarSettings>('hotjar-settings');

/**
 * Abstract Hotjar Settings Loader class. Provide an implementation when the Hotjar site
 * cannot be known at build time — for example when a single deployment serves several brands
 * and the tracking code has to be resolved from the backend at bootstrap.
 *
 * Bootstrap waits for the returned Observable to emit, so implementations must complete quickly
 * and must not depend on the user being authenticated. If the loader errors, {@link HOTJAR_SETTINGS}
 * is used instead, so the statically configured site keeps receiving data.
 */
export abstract class HotjarSettingsLoader {
  abstract getSettings(): Observable<HotjarSettings>;
}

/**
 * Default loader. Emits the statically configured {@link HOTJAR_SETTINGS} synchronously, which keeps
 * the single-site setup free of any bootstrap delay.
 */
@Injectable()
export class HotjarStaticSettingsLoader implements HotjarSettingsLoader {
  constructor(@Inject(HOTJAR_SETTINGS) private readonly settings: HotjarSettings) {}

  getSettings(): Observable<HotjarSettings> {
    return of(this.settings);
  }
}
